import React, { useEffect, useState, useCallback } from 'react';
import { supabase } from './supabaseClient';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  TextField,
  MenuItem,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  Switch,
  Slider,
  Divider
} from '@mui/material';

const AdminSystemDefaults = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Options for dropdowns
  const [embeddingModels, setEmbeddingModels] = useState([]);
  const [llmModels, setLlmModels] = useState([]);

  // LLM provider state (for two-step selection)
  const [selectedProvider, setSelectedProvider] = useState('');
  const [llmModelsByProvider, setLlmModelsByProvider] = useState({});
  const [providerOptions, setProviderOptions] = useState([]);

  // Load embedding and LLM models for dropdowns
  useEffect(() => {
    const fetchModels = async () => {
      try {
        // Fetch embedding models
        const { data: embeddingData, error: embeddingError } = await supabase
          .from('embedding_models')
          .select('model_identifier, display_label, provider')
          .eq('is_active', true)
          .order('provider', { ascending: true })
          .order('display_label', { ascending: true });

        if (embeddingError) throw embeddingError;
        setEmbeddingModels(embeddingData || []);

        // Fetch LLM models and group by provider
        const { data: llmData, error: llmError } = await supabase
          .from('llm_models')
          .select('model_identifier, display_label, provider')
          .eq('is_active', true)
          .order('provider', { ascending: true })
          .order('display_label', { ascending: true });

        if (llmError) throw llmError;
        setLlmModels(llmData || []);

        // Group LLM models by provider
        const grouped = {};
        (llmData || []).forEach((model) => {
          const provider = model.provider || 'Unknown';
          if (!grouped[provider]) grouped[provider] = [];
          grouped[provider].push({
            value: model.model_identifier,
            label: model.display_label || model.model_identifier
          });
        });

        const providers = Object.keys(grouped).sort();
        setLlmModelsByProvider(grouped);
        setProviderOptions(providers);

      } catch (err) {
        console.error('Error fetching models:', err);
        setError('Failed to load model options: ' + err.message);
      }
    };
    fetchModels();
  }, []);

  // When settings are loaded, determine the provider for the current llm_model
  useEffect(() => {
    if (settings && settings.llm_model && Object.keys(llmModelsByProvider).length > 0) {
      // Find which provider has this model
      for (const provider in llmModelsByProvider) {
        if (llmModelsByProvider[provider].some(m => m.value === settings.llm_model)) {
          setSelectedProvider(provider);
          break;
        }
      }
    }
  }, [settings, llmModelsByProvider]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data, error } = await supabase
        .from('system_default_settings')
        .select('*')
        .eq('id', 1)
        .single();

      if (error) throw error;

      if (data) {
        setSettings(data);
      } else {
        setError('No system default settings found. Please ensure migration 34 has been applied.');
      }
    } catch (err) {
      console.error('Error loading system defaults:', err);
      setError('Failed to load system defaults: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Handle provider selection change
  const handleProviderChange = (e) => {
    const newProvider = e.target.value;
    setSelectedProvider(newProvider);

    // Set the first model of the new provider as the llm_model
    const modelsForProvider = llmModelsByProvider[newProvider] || [];
    const firstModelValue = modelsForProvider.length > 0 ? modelsForProvider[0].value : '';

    setSettings(prev => ({
      ...prev,
      llm_model: firstModelValue
    }));
  };

  // Handle LLM model selection change
  const handleModelChange = (e) => {
    const { value } = e.target;
    setSettings(prev => ({
      ...prev,
      llm_model: value
    }));
  };

  const handleChange = (e, sliderValue) => {
    const { name, value, type, checked } = e.target;

    let newValue;
    if (name === 'temperature' || name === 'grounding_similarity_threshold' || name === 'cosine_similarity_threshold') {
      newValue = sliderValue !== undefined ? sliderValue : parseFloat(value);
    } else if (type === 'checkbox') {
      newValue = checked;
    } else if (type === 'number') {
      newValue = parseFloat(value);
    } else {
      newValue = value;
    }

    setSettings(prev => ({
      ...prev,
      [name]: newValue
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      // Create payload without id, created_at, updated_at
      const payload = {
        embedding_model: settings.embedding_model,
        system_prompt: settings.system_prompt,
        llm_model: settings.llm_model,
        temperature: parseFloat(settings.temperature),
        top_p: parseFloat(settings.top_p),
        total_chunks: parseInt(settings.total_chunks),
        answer_max_token: parseInt(settings.answer_max_token),
        top_k: parseInt(settings.top_k),
        cosine_similarity_threshold: parseFloat(settings.cosine_similarity_threshold),
        rag_mode: settings.rag_mode,
        enable_grounding_check: !!settings.enable_grounding_check,
        grounding_similarity_threshold: parseFloat(settings.grounding_similarity_threshold),
        enable_reranker: !!settings.enable_reranker,
        direct_system_prompt: settings.direct_system_prompt,
        faq_direct_answer: !!settings.faq_direct_answer,
        enabled_tools: settings.enabled_tools || {
          source_attribution: true,
          web_search: false,
          ikas_order_lookup: false,
          ikas_stock_check: false,
          ikas_customer_lookup: false
        }
      };

      console.log('[AdminSystemDefaults] Attempting to update system_default_settings with payload:', payload);

      const { data, error } = await supabase
        .from('system_default_settings')
        .update(payload)
        .eq('id', 1)
        .select();

      console.log('[AdminSystemDefaults] Update response:', { data, error });

      if (error) {
        console.error('[AdminSystemDefaults] Update error details:', error);
        throw error;
      }

      setSuccess('System default settings saved successfully! New users and sessions will use these values.');

      // Reload to get updated timestamp
      setTimeout(() => {
        load();
        setSuccess('');
      }, 2000);
    } catch (err) {
      console.error('Error saving system defaults:', err);
      setError('Failed to save settings: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!settings) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="error">
          {error || 'No system default settings found. Please ensure database migration 34 has been applied.'}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        System Default Settings
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        These settings are used as defaults for new users and sessions when no user-specific settings exist.
        Changes will apply to all new sessions created after saving.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Paper sx={{ p: 3 }}>
        {/* Embedding Model */}
        <TextField
          select
          fullWidth
          margin="normal"
          label="Default Embedding Model"
          name="embedding_model"
          value={settings.embedding_model || ''}
          onChange={handleChange}
          disabled={saving}
          helperText="Model used for document embeddings"
        >
          {embeddingModels.map((model) => (
            <MenuItem key={model.model_identifier} value={model.model_identifier}>
              {model.display_label}
            </MenuItem>
          ))}
        </TextField>

        {/* LLM Provider */}
        <FormControl fullWidth margin="normal" disabled={saving}>
          <InputLabel id="provider-select-label">LLM Provider</InputLabel>
          <Select
            labelId="provider-select-label"
            value={selectedProvider}
            label="LLM Provider"
            onChange={handleProviderChange}
          >
            {providerOptions.map((provider) => (
              <MenuItem key={provider} value={provider}>
                {provider}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* LLM Model */}
        <FormControl fullWidth margin="normal" disabled={saving || !selectedProvider}>
          <InputLabel id="model-select-label">LLM Model</InputLabel>
          <Select
            labelId="model-select-label"
            name="llm_model"
            value={settings.llm_model || ''}
            label="LLM Model"
            onChange={handleModelChange}
          >
            {(!selectedProvider || !llmModelsByProvider[selectedProvider] || llmModelsByProvider[selectedProvider].length === 0) && (
              <MenuItem disabled value="">
                {selectedProvider ? `No models available for ${selectedProvider}` : 'Select a provider first'}
              </MenuItem>
            )}
            {selectedProvider && llmModelsByProvider[selectedProvider] && llmModelsByProvider[selectedProvider].map((model) => (
              <MenuItem key={model.value} value={model.value}>
                {model.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5, mb: 2 }}>
          Large Language Model used for generating responses
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* System Prompt */}
        <TextField
          fullWidth
          margin="normal"
          label="Default System Prompt (RAG Mode)"
          name="system_prompt"
          value={settings.system_prompt || ''}
          onChange={handleChange}
          multiline
          rows={6}
          disabled={saving}
          helperText="Instructions for the AI when using RAG (Knowledge Base) mode"
        />

        {/* Direct System Prompt */}
        <TextField
          fullWidth
          margin="normal"
          label="Default Direct System Prompt"
          name="direct_system_prompt"
          value={settings.direct_system_prompt || ''}
          onChange={handleChange}
          multiline
          rows={3}
          disabled={saving}
          helperText="Instructions for the AI when in Direct mode (no documents)"
        />

        <Divider sx={{ my: 3 }} />

        {/* Temperature Slider */}
        <Box sx={{ mt: 3 }}>
          <Typography gutterBottom>
            Creativity (Temperature): {Number(settings.temperature || 0).toFixed(1)}
          </Typography>
          <Slider
            name="temperature"
            value={Number(settings.temperature || 0)}
            onChange={handleChange}
            step={0.1}
            marks
            min={0.0}
            max={2.0}
            valueLabelDisplay="auto"
            disabled={saving}
          />
          <Typography variant="caption" color="text.secondary">
            Higher values make output more random, lower values more focused and deterministic
          </Typography>
        </Box>

        {/* RAG Mode */}
        <TextField
          select
          fullWidth
          margin="normal"
          label="Default RAG Strategy"
          name="rag_mode"
          value={settings.rag_mode || 'similarity_filter'}
          onChange={handleChange}
          disabled={saving}
          helperText="Strategy for retrieving relevant document chunks"
        >
          <MenuItem value="similarity_filter">Similarity Filter</MenuItem>
          <MenuItem value="reranker">Reranker</MenuItem>
          <MenuItem value="two_stage_summary">Two-Stage Summary</MenuItem>
        </TextField>

        {/* Advanced Settings */}
        <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
          Advanced Settings
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            type="number"
            label="Top P"
            name="top_p"
            value={settings.top_p || 0}
            onChange={handleChange}
            disabled={saving}
            sx={{ flex: '1 1 45%' }}
            inputProps={{ step: 0.1, min: 0, max: 1 }}
          />
          <TextField
            type="number"
            label="Total Chunks"
            name="total_chunks"
            value={settings.total_chunks || 0}
            onChange={handleChange}
            disabled={saving}
            sx={{ flex: '1 1 45%' }}
            inputProps={{ min: 1 }}
          />
          <TextField
            type="number"
            label="Answer Max Tokens"
            name="answer_max_token"
            value={settings.answer_max_token || 0}
            onChange={handleChange}
            disabled={saving}
            sx={{ flex: '1 1 45%' }}
            inputProps={{ min: 1 }}
          />
          <TextField
            type="number"
            label="Top K"
            name="top_k"
            value={settings.top_k || 0}
            onChange={handleChange}
            disabled={saving}
            sx={{ flex: '1 1 45%' }}
            inputProps={{ min: 1 }}
          />
        </Box>

        {/* Cosine Similarity Threshold */}
        <Box sx={{ mt: 3 }}>
          <Typography gutterBottom>
            Cosine Similarity Threshold: {Number(settings.cosine_similarity_threshold || 0).toFixed(2)}
          </Typography>
          <Slider
            name="cosine_similarity_threshold"
            value={Number(settings.cosine_similarity_threshold || 0)}
            onChange={handleChange}
            step={0.05}
            marks
            min={0.0}
            max={1.0}
            valueLabelDisplay="auto"
            disabled={saving}
          />
          <Typography variant="caption" color="text.secondary">
            Minimum similarity score for including document chunks
          </Typography>
        </Box>

        {/* Enable Reranker */}
        <FormControlLabel
          control={
            <Switch
              checked={settings.enable_reranker || false}
              onChange={handleChange}
              name="enable_reranker"
              disabled={saving}
            />
          }
          label="Enable Reranker"
          sx={{ mt: 2 }}
        />
        <Typography variant="caption" display="block" color="text.secondary" sx={{ ml: 4, mb: 2 }}>
          Improves relevance but adds processing time
        </Typography>

        {/* Enable Grounding Check */}
        <FormControlLabel
          control={
            <Switch
              checked={settings.enable_grounding_check || false}
              onChange={handleChange}
              name="enable_grounding_check"
              disabled={saving}
            />
          }
          label="Enable Answer Grounding Check"
          sx={{ mt: 1 }}
        />
        <Typography variant="caption" display="block" color="text.secondary" sx={{ ml: 4 }}>
          Verifies answers are based on provided documents
        </Typography>

        {settings.enable_grounding_check && (
          <Box sx={{ mt: 2, ml: 4 }}>
            <Typography gutterBottom>
              Grounding Threshold: {Number(settings.grounding_similarity_threshold || 0).toFixed(2)}
            </Typography>
            <Slider
              name="grounding_similarity_threshold"
              value={Number(settings.grounding_similarity_threshold || 0)}
              onChange={handleChange}
              step={0.05}
              marks
              min={0.0}
              max={1.0}
              valueLabelDisplay="auto"
              disabled={saving}
            />
          </Box>
        )}

        {/* FAQ Direct Answer */}
        <FormControlLabel
          control={
            <Switch
              checked={settings.faq_direct_answer || false}
              onChange={handleChange}
              name="faq_direct_answer"
              disabled={saving}
            />
          }
          label="FAQ Direct Answer (Instant Responses)"
          sx={{ mt: 2 }}
        />
        <Typography variant="caption" display="block" color="text.secondary" sx={{ ml: 4, mb: 2 }}>
          When enabled, high-confidence FAQ matches return instantly without LLM processing (~2s vs ~13s).
          When disabled, FAQ answers are passed to the LLM for natural reformulation.
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* AI Tools Section */}
        <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
          Default AI Tools
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Default tool availability for new users. Individual users can override these settings.
        </Typography>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={settings.enabled_tools?.source_attribution !== false}
                onChange={(e) => {
                  setSettings(prev => ({
                    ...prev,
                    enabled_tools: {
                      ...prev.enabled_tools,
                      source_attribution: e.target.checked
                    }
                  }));
                }}
                disabled={saving}
              />
            }
            label="Source Attribution"
          />
          <FormControlLabel
            control={
              <Switch
                checked={settings.enabled_tools?.web_search || false}
                onChange={(e) => {
                  setSettings(prev => ({
                    ...prev,
                    enabled_tools: {
                      ...prev.enabled_tools,
                      web_search: e.target.checked
                    }
                  }));
                }}
                disabled={saving}
              />
            }
            label="Web Search"
          />
          <FormControlLabel
            control={
              <Switch
                checked={settings.requires_approval || false}
                onChange={(e) => {
                  setSettings(prev => ({
                    ...prev,
                    requires_approval: e.target.checked
                  }));
                }}
                disabled={saving}
              />
            }
            label="Require approval before sending answers"
          />
        </Box>

        <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2, mb: 1 }}>
          E-Commerce Tools (IKAS)
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={settings.enabled_tools?.ikas_order_lookup || false}
                onChange={(e) => {
                  setSettings(prev => ({
                    ...prev,
                    enabled_tools: {
                      ...prev.enabled_tools,
                      ikas_order_lookup: e.target.checked
                    }
                  }));
                }}
                disabled={saving}
              />
            }
            label="Order Lookup"
          />
          <FormControlLabel
            control={
              <Switch
                checked={settings.enabled_tools?.ikas_stock_check || false}
                onChange={(e) => {
                  setSettings(prev => ({
                    ...prev,
                    enabled_tools: {
                      ...prev.enabled_tools,
                      ikas_stock_check: e.target.checked
                    }
                  }));
                }}
                disabled={saving}
              />
            }
            label="Stock Check"
          />
          <FormControlLabel
            control={
              <Switch
                checked={settings.enabled_tools?.ikas_customer_lookup || false}
                onChange={(e) => {
                  setSettings(prev => ({
                    ...prev,
                    enabled_tools: {
                      ...prev.enabled_tools,
                      ikas_customer_lookup: e.target.checked
                    }
                  }));
                }}
                disabled={saving}
              />
            }
            label="Customer Lookup"
          />
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Metadata */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Last Updated: {settings.updated_at ? new Date(settings.updated_at).toLocaleString() : 'N/A'}
          </Typography>
        </Box>

        {/* Save Button */}
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={load}
            disabled={saving}
          >
            Reset
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? <CircularProgress size={24} /> : 'Save System Defaults'}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default AdminSystemDefaults;

